import React from 'react';
import { Box, Typography, Button, Paper, Container } from '@mui/material';
import { ErrorOutline, Home, Refresh } from '@mui/icons-material';
import * as Sentry from '@sentry/react';

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log to Sentry if configured
    if (typeof Sentry !== 'undefined' && Sentry.captureException) {
      Sentry.captureException(error, { 
        contexts: { react: { componentStack: errorInfo.componentStack } } 
      });
    }
    
    this.setState({ errorInfo });
    if (this.props.onError) this.props.onError(error, errorInfo);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: 6, 
              textAlign: 'center',
              bgcolor: 'error.50',
              border: '2px solid',
              borderColor: 'error.200'
            }}
          >
            <ErrorOutline 
              sx={{ 
                fontSize: 80, 
                color: 'error.main', 
                mb: 3 
              }} 
            />
            
            <Typography variant="h4" fontWeight={700} color="error.main" gutterBottom>
              Oops! Something went wrong
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
              We're sorry for the inconvenience. An unexpected error occurred. 
              Our team has been notified and is working to fix the issue.
            </Typography>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box sx={{ mb: 4, textAlign: 'left', bgcolor: 'grey.100', p: 2, borderRadius: 2, overflow: 'auto' }}>
                <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', color: 'error.dark' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo && '\n\n' + this.state.errorInfo.componentStack}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Refresh />}
                onClick={this.handleReset}
                sx={{ borderRadius: 99, px: 4 }}
              >
                Try Again
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Home />}
                onClick={() => window.location.href = '/'}
                sx={{ borderRadius: 99, px: 4 }}
              >
                Go to Homepage
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
              If the problem persists, please contact support at{' '}
              <a href="mailto:support@movd.ca" style={{ color: '#1976d2' }}>
                support@movd.ca
              </a>
            </Typography>
          </Paper>
        </Container>
      );
    }
    return this.props.children;
  }
}
